import moment from "moment";

import {
    REPORT_SENSOR_REGISTRY,
    REPORT_SUMMARY_ITEMS,
    ROOM_TAG_PREFIX,
} from "constant/report";
import { TagData } from "generated/models";
import { Device } from "types";
import {
    NullableMoment,
    NullableNumber,
    ReportChartSeries,
    ReportIndividualData,
    ReportItemsState,
    ReportItemStateData,
    ReportItemSummaryType,
    ReportItemType,
    ReportOverallData,
    ReportOverviewData,
    ReportPDFIndividualData,
    ReportPDFOverviewData,
    ReportPDFSummaryInfo,
    ReportRoom,
    ReportSummaryInfo,
    ReportTimeRangeType,
} from "types/Reports";
import { DATE_TIME_FORMATS } from "constant";
import {
    computeNullableAverage,
    getDataOfReportChartSeries,
    getProcessedSeriesByTimeRange,
    getStatusOrOccupiedSensorFromLDSU,
    isReportIndividualDatasValid,
    isReportOverallDataValid,
} from "./report";
import {
    getTotalMaxFromConsumptionArray,
    isDataArrayValid,
    getLowestHighestAndSortIndividualData,
} from "./reportArray";
import { computeDayInMonthByIndex } from "./reportCompute";
import { convertReportIndividualItemsToRows } from "./reportConvert";
import { getHighestOrLowestNameDisplay } from "./reportString";

export const transformRawRoomToReportRoom = (
    roomSensors: Device[],
    rawRoom: TagData,
    allDevices: Device[],
): ReportRoom => {
    const room: ReportRoom = {
        roomUUId: rawRoom.uuid,
        roomValue: rawRoom.value,
        roomName: rawRoom.value.replace(ROOM_TAG_PREFIX, ''),
        createdTime: rawRoom.create_time,
        modifiedTime: rawRoom.modify_time,
        gatewayId: '',
        roomOrganizationId: rawRoom.organisation_id,
        statusDevice: null,
        occupancyDevice: null,
    };

    if (!rawRoom.devices) {
        return room;
    }

    for (const device of rawRoom.devices) {
        // There are 2 format
        // 1 - Gateway ID, LDSU ID, SAID
        // 2 - LDSU ID, SAID
        const length = device.length;
        const deviceLDSUID = device[length - 2];
        const deviceSAID = device[length - 1];

        const { statusDevice, occupancyDevice } = getStatusOrOccupiedSensorFromLDSU(
            roomSensors,
            `${deviceLDSUID}`,
            Number(deviceSAID),
        );

        if (statusDevice) {
            room.statusDevice = statusDevice;
            room.gatewayId = statusDevice.gatewayId;
        }

        if (occupancyDevice) {
            room.occupancyDevice = occupancyDevice;
            room.gatewayId = occupancyDevice.gatewayId;
        }

        if (room.statusDevice && room.occupancyDevice) {
            break;
        }

        if (room.gatewayId === '' && allDevices?.length > 0) {
            // fill in the gatewayId of the room
            const foundDevice = allDevices.find((d) => d.ldsuUID === deviceLDSUID && d.said === deviceSAID);
            if (foundDevice) {
                room.gatewayId = foundDevice.gatewayId;
            }
        }
    }

    return room;
};

export const transformReportSeriesToOverallData = (
    total: NullableNumber,
    totalWeekdays: NullableNumber,
    totalWeekends: NullableNumber,
    series: ReportChartSeries[],
) => {
    const processed = getProcessedSeriesByTimeRange(series);

    return {
        all: { ...processed.all, average: computeNullableAverage(processed.all.count, total) },
        weekdays: { ...processed.weekdays, average: computeNullableAverage(processed.weekdays.count, totalWeekdays) },
        weekends: { ...processed.weekends, average: computeNullableAverage(processed.weekends.count, totalWeekends) },
    };
};

export const transformConsumptionDataToOverallData = (
    total: NullableNumber,
    totalWeekdays: NullableNumber,
    totalWeekends: NullableNumber,
    series: ReportChartSeries[],
    categories: string[],
    endDate: moment.Moment,
    prevAverage: {
        averageAll: NullableNumber;
        averageWeekdays: NullableNumber;
        averageWeekends: NullableNumber;
    },
) => {
    // aggregate the consumption series data to get min, max, average
    const aggregatedOverallData = transformReportSeriesToOverallData(
        total,
        totalWeekdays,
        totalWeekends,
        series,
    );

    // get date for min and max
    const daysInMonth = endDate.daysInMonth();
    const lastDayOfMonth = moment.unix(endDate.unix());
    const mapIndexesToDates = (indexes: number[]) =>
        indexes.map((idx) => computeDayInMonthByIndex(Number.parseInt(categories[idx]), daysInMonth, lastDayOfMonth));

    const buildTimeRangeData = (
        agg: typeof aggregatedOverallData.all,
        prevAvg: NullableNumber,
    ) => ({
        data: agg.data,
        highestItem: { value: agg.highestValue, dates: mapIndexesToDates(agg.highestIndex) },
        lowestItem: { value: agg.lowestValue, dates: mapIndexesToDates(agg.lowestIndex) },
        average: agg.average,
        prevAverage: prevAvg,
    });

    return {
        categories,
        all: buildTimeRangeData(aggregatedOverallData.all, prevAverage.averageAll),
        weekdays: buildTimeRangeData(aggregatedOverallData.weekdays, prevAverage.averageWeekdays),
        weekends: buildTimeRangeData(aggregatedOverallData.weekends, prevAverage.averageWeekends),
    };
};

export const transformConsumptionDataToIndividualData = (
    series: ReportChartSeries[],
    categories: string[],
    endDate: moment.Moment,
): ReportIndividualData[] => {
    if (series.length === 0) {
        return [];
    }

    const lastDayOfMonthIndex = endDate.daysInMonth();
    const lastDayOfMonth = moment.unix(endDate.unix());

    const getHighestDate = (highestIndex: number | null): NullableMoment => {
        if (highestIndex === null) return null;
        return computeDayInMonthByIndex(
            Number.parseInt(categories[highestIndex]),
            lastDayOfMonthIndex,
            lastDayOfMonth,
        );
    };

    return series.map((item) => {
        const processedAll = getTotalMaxFromConsumptionArray(item.data);
        const processedWeekdays = getTotalMaxFromConsumptionArray(item.weekdaysData);
        const processedWeekends = getTotalMaxFromConsumptionArray(item.weekendsData);

        const buildTimeRange = (processed: typeof processedAll) => ({
            value: processed.totalValue,
            date: getHighestDate(processed.highestIndex),
            average: computeNullableAverage(processed.count, processed.totalValue),
        });

        return {
            name: item.name,
            id: item.id,
            all: buildTimeRange(processedAll),
            weekdays: buildTimeRange(processedWeekdays),
            weekends: buildTimeRange(processedWeekends),
        };
    });
};

export const transformDataToOUCParams = (
    type: ReportItemSummaryType,
    timeRangeType: ReportTimeRangeType,
    summaryInfo: ReportSummaryInfo,
    selectedData: ReportOverallData,
) => {
    return {
        data: selectedData[timeRangeType].data,
        categories: selectedData.categories,

        total: summaryInfo[timeRangeType][type].total,

        percent: summaryInfo[timeRangeType][type].percent,
        highestItem: { ...selectedData[timeRangeType].highestItem },
        lowestItem: { ...selectedData[timeRangeType].lowestItem },
        average: selectedData[timeRangeType].average,
        prevAverage: selectedData[timeRangeType].prevAverage,
    };
};

export const transformReportDataToPDFIndividualData = (
    itemsData: ReportItemStateData,
    timeRangeType: ReportTimeRangeType,
    categories: string[],
) => {
    const pdfIndividualData: ReportPDFIndividualData = {
        room: [],
        energy: [],
        water: [],
        waterLeakage: [],
        temperature: [],
        humidity: [],
        co2: [],
        pressure: [],
        tvoc: [],
        pm1: [],
        'pm2.5': [],
        pm10: [],
    };

    const types = Object.keys(itemsData) as ReportItemType[];

    for (const type of types) {
        for (const series of itemsData[type].chartSeries) {
            const data = getDataOfReportChartSeries(series, timeRangeType);

            const isValid = isDataArrayValid(data);

            if (!isValid) {
                pdfIndividualData[type].push({
                    id: series.id,
                    name: series.name,
                    data: [],
                    prevData: [],
                    categories: [...categories],
                });

                continue;
            }

            let prevSeries = itemsData[type].prevChartSeries.find((s) => s.id === series.id);
            let prevData: NullableNumber[] = [];

            if (prevSeries) {
                prevData = getDataOfReportChartSeries(prevSeries, timeRangeType);
            }

            pdfIndividualData[type].push({
                id: series.id,
                name: series.name,
                data,
                prevData,
                categories: [...categories],
            });
        }
    }

    return pdfIndividualData;
};

export const transformReportDataToPDFSummaryInfo = (
    appliedTimeRangeType: ReportTimeRangeType,
    summaryInfo: ReportSummaryInfo,
    items: ReportItemsState,
) => {
    const summaryPDFInfo: ReportPDFSummaryInfo = {
        room: {
            title: 'Room Occupancy',
            cardType: 'room',
            totalTextColor: REPORT_SENSOR_REGISTRY.room.colorHex,
            unit: '',
            item: {
                total: null,
                previousTotal: null,
                percent: null,
            },
        },
        energy: {
            title: 'Energy Consumption',
            cardType: 'energy',
            totalTextColor: REPORT_SENSOR_REGISTRY.energy.colorHex,
            unit: REPORT_SENSOR_REGISTRY.energy.unit,
            item: {
                total: null,
                previousTotal: null,
                percent: null,
            },
        },
        water: {
            title: 'Water Consumption',
            cardType: 'water',
            totalTextColor: REPORT_SENSOR_REGISTRY.water.colorHex,
            unit: REPORT_SENSOR_REGISTRY.water.unit,
            item: {
                total: null,
                previousTotal: null,
                percent: null,
            },
        },
        waterLeakage: {
            title: 'Water Leakage Detection',
            cardType: 'waterLeakage',
            totalTextColor: REPORT_SENSOR_REGISTRY.waterLeakage.colorHex,
            unit: '',
            item: {
                total: null,
                previousTotal: null,
                percent: null,
            },
        },
    };

    if (items.room.appliedIds.length > 0) {
        summaryPDFInfo.room = {
            title: 'Room Occupancy',
            cardType: 'room',
            totalTextColor: REPORT_SENSOR_REGISTRY.room.colorHex,
            unit: '',
            item: {...summaryInfo[appliedTimeRangeType]['room']},
        };
    }
    if (items.energy.appliedIds.length > 0) {
        summaryPDFInfo.energy = {
            title: 'Energy Consumption',
            cardType: 'energy',
            totalTextColor: REPORT_SENSOR_REGISTRY.energy.colorHex,
            unit: REPORT_SENSOR_REGISTRY.energy.unit,
            item: {...summaryInfo[appliedTimeRangeType]['energy']},
        };
    }
    if (items.water.appliedIds.length > 0) {
        summaryPDFInfo.water = {
            title: 'Water Consumption',
            cardType: 'water',
            totalTextColor: REPORT_SENSOR_REGISTRY.water.colorHex,
            unit: REPORT_SENSOR_REGISTRY.water.unit,
            item: {...summaryInfo[appliedTimeRangeType]['water']},
        };
    }
    if (items.waterLeakage.appliedIds.length > 0) {
        summaryPDFInfo.waterLeakage = {
            title: 'Water Leakage Detection',
            cardType: 'waterLeakage',
            totalTextColor: REPORT_SENSOR_REGISTRY.waterLeakage.colorHex,
            unit: '',
            item: {...summaryInfo[appliedTimeRangeType]['waterLeakage']},
        };
    }

    return summaryPDFInfo;
};

export const transformReportDataToPDFOverviewData = (
    items: ReportItemsState,
    overviewData: ReportOverviewData,
    timeRangeType: ReportTimeRangeType,
    summaryInfo: ReportSummaryInfo,
) => {
    const overviewInfo: ReportPDFOverviewData = {
        room: undefined,
        energy: undefined,
        water: undefined,
        waterLeakage: undefined,
    };

    for (const type of REPORT_SUMMARY_ITEMS) {
        if (
            items[type].appliedIds.length > 0 &&
            isReportOverallDataValid(overviewData[type].overallData, timeRangeType) &&
            isReportIndividualDatasValid(overviewData[type].individualData, timeRangeType)
        ) {
            const overallUsageInfo = transformDataToOUCParams(type, timeRangeType, summaryInfo, overviewData[type].overallData);

            const {
                highest,
                lowest,
                sortedData,
            } = getLowestHighestAndSortIndividualData(timeRangeType, overviewData[type].individualData);

            const individualUsageInfo = {
                highestItemName: getHighestOrLowestNameDisplay(highest.names, type),
                highestItemValue: highest.value,
                lowestItemName: getHighestOrLowestNameDisplay(lowest.names, type),
                lowestItemValue: lowest.value,
                headers: REPORT_SENSOR_REGISTRY[type].pdfOverview!.individualTableHeaders,
                rows: convertReportIndividualItemsToRows(sortedData, DATE_TIME_FORMATS.DATE_ONLY, type),
            };

            overviewInfo[type] = {
                overallData: overallUsageInfo,
                individualData: individualUsageInfo,
            };

            continue;
        }

        if (items[type].appliedIds.length > 0) {
            // input for invalid data
            overviewInfo[type] = {
                overallData: {
                    total: null,
                    percent: null,
                    highestItem: {
                        dates: [],
                        value: null,
                    },
                    lowestItem: {
                        dates: [],
                        value: null,
                    },
                    average: null,
                    prevAverage: null,
                    categories: [],
                    data: [],
                },
                individualData: {
                    highestItemName: '',
                    highestItemValue: null,
                    lowestItemName: '',
                    lowestItemValue: null,
                    headers: [],
                    rows: [],
                },
            };
        }
    }

    return overviewInfo;
};
